import { format } from 'date-fns'
import React from 'react'
import {
  ExternalUserFieldAccess,
  FormField,
  GeckoAssetModelType,
  getFormFieldByDataKey,
  InputType,
  MachinedForgingSimpleAssetModelJSON,
  RolodexEntryJson,
} from 'types'
import { MachinedForgingComponentStage } from 'types/assetModel/components/machinedForging/geckoMachinedForgingSimple'
import {
  assetModelFormFields,
  GeckoAssetModel,
} from 'types/entries/geckoAssetModel'
import { RolodexEntryPutJson } from 'types/rolodex/rolodexEntries'
import { parseDate } from 'utils/parsingHelpers'
import {
  optionalDateStringWithRegex,
  optionalStringSchema,
} from 'utils/zodHelpers'

import { GeckoComponentModelType } from './assetModelEnums'

function getFirstComponent<T>(
  components: Record<string, T> | undefined
): T | undefined {
  if (!components) {
    return undefined
  }
  const componentKeys = Object.keys(components)
  if (componentKeys.length === 0) {
    return undefined
  }
  const firstKey = componentKeys[0]
  return components[firstKey]
}

export const machinedForgingStageColorMap = new Map<string, string>([
  ['UNMILLED', 'rgba(255,193,7,1)'],
  ['MILLED', 'rgba(128,204,137,1)'],
  ['WELD', 'rgba(64,183,78,1)'],
  ['FINAL ASSEMBLY', 'rgba(20,106,54,1)'],
])

interface MachinedForgingStageContainerProps {
  bgColor?: string
  textColor?: string
  children?: React.ReactNode
}

export const MachinedForgingStageContainer: React.FC<
  MachinedForgingStageContainerProps
> = ({ bgColor, textColor, children }) => {
  const style: React.CSSProperties = {
    padding: '0.2rem 0.4rem 0.2rem 0.4rem',
    borderRadius: '0.2rem',
    backgroundColor: bgColor ?? '#f0f0f0',
    color: textColor ?? '#000',
    width: 'fit-content',
    minHeight: '1rem',
    minWidth: '1rem',
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textTransform: 'capitalize',
    gap: '0.25rem',
    cursor: 'default',
  }

  return React.createElement('div', { style }, children)
}

enum MachinedForgingStatus {
  InitialWorkOrder = 'Initial Work Order Received',
  AwaitingInspection = 'Awaiting Inspection',
  AwaitingMillingPlan = 'Awaiting Milling Plan',
  MillingPlanProposed = 'Milling Plan Proposed',
  MillingPlanApproved = 'Milling Plan Approved',
  MillingComplete = 'Milling Complete',
  InspectionNeedsReview = 'Inspection Needs Review',
  ReadyForRelease = 'Ready for Release',
}

export type GeckoMachinedForgingAssetModelJSON = {
  name: string
  type: GeckoAssetModelType.MachinedForging
  components: Record<string, MachinedForgingSimpleAssetModelJSON>
  effective_date: string
  status: string | undefined
  installation_date: string | undefined
  compass_orientation: number | undefined
  plant_north_compass_orientation: number | undefined
}

export const MachinedForgingAvailableComponents: GeckoComponentModelType[] = [
  GeckoComponentModelType.MachinedForgingSimple,
]

export const NewMachinedForgingAssetModelJSON = {
  name: 'New Asset Model Machined Forging',
  type: GeckoAssetModelType.MachinedForging,
  components: {},
  effective_date: format(Date.now(), 'yyyy-MM-dd'),
  status: undefined,
  installation_date: undefined,
} as GeckoMachinedForgingAssetModelJSON

export class GeckoMachinedForgingAssetModel extends GeckoAssetModel {
  private constructor(json: RolodexEntryJson) {
    super(json)
  }

  public static CreateFromJSON(
    json: RolodexEntryJson
  ): GeckoMachinedForgingAssetModel {
    return new GeckoMachinedForgingAssetModel(json)
  }

  public asJSON(): GeckoMachinedForgingAssetModelJSON {
    return generateMachinedForgingAssetModel(
      this.data.name,
      this.raw.data.components as Record<
        string,
        MachinedForgingSimpleAssetModelJSON
      >,
      this.data.effective_date,
      this.data.status as string | undefined,
      this.data.installation_date as string | undefined,
      this.data.compass_orientation,
      this.data.plant_north_compass_orientation
    )
  }
}

export function generateMachinedForgingAssetModel(
  name: string,
  components: Record<string, MachinedForgingSimpleAssetModelJSON>,
  effective_date: Date,
  status: string | undefined,
  installation_date: string | undefined,
  compass_orientation: number | undefined,
  plant_north_compass_orientation: number | undefined
): GeckoMachinedForgingAssetModelJSON {
  const machinedForgingEffectiveDate = effective_date
    .toISOString()
    .split('T')[0]
  return {
    name: name,
    type: GeckoAssetModelType.MachinedForging,
    components: components,
    effective_date: machinedForgingEffectiveDate,
    status: status,
    installation_date: installation_date,
    compass_orientation: compass_orientation,
    plant_north_compass_orientation: plant_north_compass_orientation,
  }
}

export const machinedForgingModelFormFields = (
  assetModelJSON: GeckoMachinedForgingAssetModelJSON
): FormField[] => {
  const assetModelfields = assetModelFormFields(assetModelJSON)
  const firstComponent = getFirstComponent(assetModelJSON.components)
  const initialComponentStage = firstComponent?.stage

  const machinedForgingModelFormFields: FormField[] = [
    {
      dataKey: 'status',
      label: 'Status',
      value: assetModelJSON.status,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      options: Object.values(MachinedForgingStatus).map((status) => ({
        label: status,
        value: status,
      })),
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
    },
    {
      dataKey: 'component_stage',
      label: 'Component Stage',
      value: initialComponentStage,
      unit_magnitude: null,
      validationRule: optionalStringSchema,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Dropdown,
      options: Object.values(MachinedForgingComponentStage).map((stage) => ({
        label: React.createElement(
          MachinedForgingStageContainer,
          {
            bgColor: machinedForgingStageColorMap.get(stage),
            textColor: '#000',
          },
          stage
        ),
        value: stage,
      })),
    },
    {
      dataKey: 'effective_date',
      label: 'Effective Date',
      value: assetModelJSON.effective_date,
      unit_magnitude: null,
      validationRule: optionalDateStringWithRegex,
      externalUserAccess: ExternalUserFieldAccess.ReadWrite,
      isNeededForDerivedLayer: false,
      inputType: InputType.Date,
    },
  ]
  return [...assetModelfields, ...machinedForgingModelFormFields]
}

export const MachinedForgingJSONFromFormFields = (
  assetModelJSON: GeckoMachinedForgingAssetModelJSON,
  formFields: FormField[]
): GeckoMachinedForgingAssetModelJSON => {
  const name = getFormFieldByDataKey(formFields, 'name')?.value as string
  const effectiveDate = parseDate(
    getFormFieldByDataKey(formFields, 'effective_date')?.value as string
  ) as Date
  const status = getFormFieldByDataKey(formFields, 'status')?.value as
    | string
    | undefined
  const newComponentStage = getFormFieldByDataKey(formFields, 'component_stage')
    ?.value as string | undefined
  const installationDate = getFormFieldByDataKey(
    formFields,
    'installation_date'
  )?.value as string | undefined
  const compassOrientation = getFormFieldByDataKey(
    formFields,
    'compass_orientation'
  )?.value as number | undefined
  const plantNorthCompassOrientation = getFormFieldByDataKey(
    formFields,
    'plant_north_compass_orientation'
  )?.value as number | undefined

  const updatedComponents = structuredClone(
    assetModelJSON.components
  ) as Record<string, MachinedForgingSimpleAssetModelJSON>

  const firstComponentToUpdate = getFirstComponent(updatedComponents)
  if (firstComponentToUpdate) {
    firstComponentToUpdate.stage = newComponentStage
  }

  return generateMachinedForgingAssetModel(
    name,
    updatedComponents,
    effectiveDate,
    status,
    installationDate,
    compassOrientation,
    plantNorthCompassOrientation
  )
}

export function regenerateMachinedForgingAssetModel(
  assetModel: GeckoAssetModel
): RolodexEntryPutJson {
  const rawAssetModel = assetModel.raw
  if (!rawAssetModel.data.components) {
    throw new Error(
      'Unable to regenerate this Asset Model! Components seem to be undefined.'
    )
  }
  const regeneratedComponents = {} as Record<string, unknown>
  for (const [componentID, component] of Object.entries(
    rawAssetModel.data.components
  )) {
    switch (component.type) {
      case GeckoComponentModelType.MachinedForgingSimple:
        regeneratedComponents[componentID] =
          regenerateMachinedForgingSimpleComponent(component)
        break
      default:
        throw new Error(
          'Unable to determine this type of Machined Forging Component!'
        )
    }
  }
  assetModel.raw.data.components = regeneratedComponents
  return assetModel.raw
}

export function regenerateMachinedForgingSimpleComponent(
  machinedForgingSimple: Record<string, unknown>
): Record<string, unknown> {
  return machinedForgingSimple
}
